import { useEffect, useState, type RefObject } from 'react'

export interface UseInViewOptions {
  /** Start counting an element as visible this far before it reaches the viewport. */
  rootMargin?: string
}

/**
 * Whether an element has come into (or near) view. Latches: once true it stays true, because the
 * callers use it to decide when to START work, and undoing a resolve on scroll-out saves nothing.
 *
 * Where `IntersectionObserver` does not exist (jsdom, very old browsers) everything counts as
 * visible — degrading to "load it all" is correct, degrading to "load nothing" is not.
 */
export function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { rootMargin = '200px' }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    if (inView) return

    const element = ref.current

    if (!element || typeof IntersectionObserver === 'undefined') {
      setInView(true)

      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { rootMargin },
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [inView, ref, rootMargin])

  return inView
}
